import 'nprogress/nprogress.css'; // progress bar style

import NProgress from 'nprogress'; // progress bar
import { MessagePlugin } from 'tdesign-vue-next';
import type { RouteRecordRaw } from 'vue-router';

import router from '@/router';
import { getPermissionStore, useUserStore } from '@/store';
import type { RouteMeta } from '@/types/interface';
import { PAGE_NOT_FOUND_ROUTE } from '@/utils/route/constant';

NProgress.configure({ showSpinner: false });

router.beforeEach(async (to, from, next) => {
  NProgress.start();

  const permissionStore = getPermissionStore();
  const { whiteListRouters } = permissionStore;

  const userStore = useUserStore();

  if (userStore.token) {
    if (to.path === '/login') {
      next();
      return;
    }
    try {
      await userStore.getUserInfo();

      const { asyncRoutes } = permissionStore;

      if (asyncRoutes && asyncRoutes.length === 0) {
        let routeList: Array<RouteRecordRaw> = [];
        try {
          routeList = await permissionStore.buildAsyncRoutes();
        } catch (error) {
          if (to.path.startsWith('/result')) {
            next();
            return;
          }
          throw error;
        }
        routeList.forEach((item: RouteRecordRaw) => {
          router.addRoute(item);
        });

        if (to.path.startsWith('/result')) {
          next({ ...to, replace: true });
          return;
        }
        if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
          // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
          next({ path: to.fullPath, replace: true, query: to.query });
          return;
        } else {
          const redirect = decodeURIComponent((from.query.redirect || to.path) as string);
          next(to.path === redirect ? { ...to, replace: true } : { path: redirect, query: to.query });
          return;
        }
      }
      if (router.hasRoute(to.name)) {
        next();
      } else {
        next(`/`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '路由权限加载失败';
      MessagePlugin.error(errorMessage);
      if (errorMessage.includes('401') || errorMessage.includes('token') || errorMessage.includes('未登录')) {
        userStore.logout();
        permissionStore.restoreRoutes();
        next({
          path: '/login',
          query: { redirect: encodeURIComponent(to.fullPath) },
        });
      } else {
        next({ path: '/result/403', replace: true });
      }
      NProgress.done();
    }
  } else {
    /* white list router */
    if (whiteListRouters.includes(to.path)) {
      next();
    } else {
      next({
        path: '/login',
        query: { redirect: encodeURIComponent(to.fullPath) },
      });
    }
    NProgress.done();
  }
});

router.afterEach((to) => {
  // 访问登录页时清理登录态。
  // 但如果是因 401/超时被重定向到 /login（非用户主动退出），
  // request 拦截器已经调用了 logout，此处不再重复清理——
  // 避免用户在多标签页操作时 token 被误清导致连锁掉线。
  if (to.path === '/login') {
    const userStore = useUserStore();
    if (userStore.token) {
      // token 仍存在说明是主动访问登录页（如点击退出），才清理
      const permissionStore = getPermissionStore();
      userStore.logout();
      permissionStore.restoreRoutes();
    }
  }

  // 设置页面标题为当前菜单名称
  const title = to.meta?.title as RouteMeta['title'];
  if (title) {
    document.title = typeof title === 'string' ? title : title.zh_CN || Object.values(title)[0] || '';
  }

  NProgress.done();
});
